import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { Fragment, useState, type ReactNode } from 'react';
import { errorText } from '../lib/api';
import { useRunScope } from '../lib/characters';
import { when } from '../lib/datetime';
import { useDraft } from '../lib/draft';
import {
  grantRescueClaim,
  playerBuildOrders,
  playerOccupancies,
  playerQuests,
  playerRescueAttempts,
  replacePiece,
  voidRescueClaim,
  voidSite,
  type BuildOrderAdminRow,
  type OccupancyAdminRow,
  type QuestAdminRow,
  type RescueAttemptAdminRow,
} from '../lib/s2Api';
import { playerLedger, type LedgerRow } from '../lib/storyApi';
import { Plate, ReasonEditor, TonePlate } from './PlayerStorySection';

const th = 'px-2 py-1 text-left';
const td = 'px-2 py-1';
const btn =
  'rounded border-2 border-ink px-2 py-0.5 text-xs font-semibold hover:bg-caption/40 disabled:opacity-40';
const tab =
  'rounded border-2 border-ink px-3 py-1 text-sm font-semibold hover:bg-caption/50 aria-selected:bg-ink aria-selected:text-newsprint';

const list = <T,>(d: unknown): T[] => (Array.isArray(d) ? (d as T[]) : []);
/** Run-scoped keys (ADR 0069: `runId` = the selected character). */
const featureKey = (playerId: string, what: string, runId?: string) => [
  'admin-player-s2',
  playerId,
  what,
  runId,
];

/** A 404 or a failed fetch means the Step 2 endpoint isn't deployed yet, not a broken page. */
const notThereYet = (e: unknown) =>
  e instanceof TypeError || (e instanceof Error && /^HTTP 404\b|^Not Found\b/.test(e.message));

function Loaded<T>({
  q,
  endpoint,
  owner,
  empty,
  children,
}: {
  q: UseQueryResult<T[]>;
  endpoint: string;
  owner: string;
  empty: string;
  children: (rows: T[]) => ReactNode;
}) {
  if (q.isPending) return <p className="text-sm">Loading&hellip;</p>;
  if (q.isError)
    return notThereYet(q.error) ? (
      <p className="text-sm text-bruise">
        Not available yet: this needs the {owner} API (<code>{endpoint}</code>)
      </p>
    ) : (
      <p className="text-sm text-blam">{errorText(q.error)}</p>
    );
  if (q.data.length === 0) return <p className="text-sm">{empty}</p>;
  return <>{children(q.data)}</>;
}

type View = 'chance' | 'occupied' | 'rescues' | 'quests' | 'orders';
const VIEWS: [View, string][] = [
  ['chance', 'Chance events'],
  ['occupied', 'Occupancies'],
  ['rescues', 'Rescue attempts'],
  ['quests', 'Key-part hunts'],
  ['orders', 'Build orders'],
];

/**
 * Step 2 player support (chance.html, occupied.html, rescue.html, macguffin.html, crafting.html §5 Admin): the run's chance events
 * (ledger rows of chance chains), occupancy rows (what was rolled, what happened), rescue attempts with their hidden variables and the
 * rejection reason (grant or void a held claim), the quest inspector with the piece ledger (re-place a piece, void a site), and build
 * orders. Every staff action takes a reason and a `confirm()`, and the API audits it.
 */
export function PlayerFeaturesSection({ playerId }: { playerId: string }) {
  const [view, setView] = useState<View>('chance');
  return (
    <section className="overflow-x-auto rounded border-2 border-ink bg-white p-4 lg:col-span-2">
      <div className="mb-2 flex flex-wrap items-center gap-2" role="tablist">
        <h2 className="mr-2 font-semibold">Missions and workshop</h2>
        {VIEWS.map(([v, label]) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={view === v}
            className={tab}
            onClick={() => setView(v)}
          >
            {label}
          </button>
        ))}
      </div>
      {view === 'chance' && <ChanceTab playerId={playerId} />}
      {view === 'occupied' && <OccupiedTab playerId={playerId} />}
      {view === 'rescues' && <RescuesTab playerId={playerId} />}
      {view === 'quests' && <QuestsTab playerId={playerId} />}
      {view === 'orders' && <OrdersTab playerId={playerId} />}
    </section>
  );
}

/** Ledger rows whose chain is a chance event's (from the content draft). */
function ChanceTab({ playerId }: { playerId: string }) {
  const { client, runId } = useRunScope();
  const draft = useDraft();
  const events = draft?.bundle.chanceEvents?.events ?? [];
  const byChain = new Map(events.filter((e) => e?.chainId).map((e) => [e.chainId!, e.id ?? '']));
  const q = useQuery({
    queryKey: ['admin-player-story', playerId, 'ledger', runId],
    queryFn: async () => list<LedgerRow>(await playerLedger(client, playerId)),
  });
  if (!draft)
    return (
      <p className="text-sm text-bruise">
        Open a content editor once to load the content (the chance event list comes from it).
      </p>
    );
  const filtered: UseQueryResult<LedgerRow[]> = q.isSuccess
    ? ({ ...q, data: q.data.filter((r) => byChain.has(r.chainId)) } as UseQueryResult<LedgerRow[]>)
    : q;
  return (
    <Loaded
      q={filtered}
      endpoint="GET /admin/players/{id}/ledger"
      owner="S1-ENG"
      empty="No chance events this run."
    >
      {(rows) => (
        <table className="w-full text-xs">
          <thead className="text-bruise">
            <tr>
              <th className={th}>When</th>
              <th className={th}>Event</th>
              <th className={th}>Node</th>
              <th className={th}>Option</th>
              <th className={th}>Outcome</th>
              <th className={th}>Tone</th>
              <th className={th}>Truth</th>
              <th className={th}>Raid</th>
              <th className={th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-newsprint align-top">
                <td className={`${td} font-mono`}>
                  {when(r.createdAt) || new Date(r.chosenAtMs).toLocaleString()}
                </td>
                <td className={`${td} font-mono`}>{byChain.get(r.chainId)}</td>
                <td className={`${td} font-mono`}>{r.nodeKey}</td>
                <td className={`${td} font-mono`}>
                  {r.optionId}
                  {r.timedOut && <span className="ml-1 text-blam">(timed out)</span>}
                </td>
                <td className={`${td} font-mono`}>{r.outcomeId}</td>
                <td className={td}>
                  <TonePlate tone={r.tone} />
                </td>
                <td className={`${td} font-mono`}>
                  {Object.entries(r.truth ?? {})
                    .map(([k, v]) => `${k}=${v}`)
                    .join(', ')}
                </td>
                <td className={`${td} font-mono`}>
                  {r.raidId ?? '-'}
                  {r.raidOutcome && <div className="text-bruise">{r.raidOutcome}</div>}
                </td>
                <td className={td}>
                  <Plate s={r.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Loaded>
  );
}

function OccupiedTab({ playerId }: { playerId: string }) {
  const { client, runId } = useRunScope();
  const q = useQuery({
    queryKey: featureKey(playerId, 'occupied', runId),
    queryFn: async () => list<OccupancyAdminRow>(await playerOccupancies(client, playerId)),
  });
  return (
    <Loaded
      q={q}
      endpoint="GET /admin/players/{id}/occupancies"
      owner="S2-HUM"
      empty="No occupied buildings rolled."
    >
      {(rows) => (
        <table className="w-full text-xs">
          <thead className="text-bruise">
            <tr>
              <th className={th}>Rolled</th>
              <th className={th}>Building</th>
              <th className={th}>Crew</th>
              <th className={th}>Disposition</th>
              <th className={th}>Tells</th>
              <th className={th}>Stances</th>
              <th className={th}>State</th>
              <th className={th}>Raids</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.id} className="border-t border-newsprint align-top">
                <td className={`${td} font-mono`}>{when(o.createdAt)}</td>
                <td className={td}>
                  {o.buildingName ?? 'unnamed'}{' '}
                  <span className="font-mono text-bruise">#{o.buildingId}</span>
                </td>
                <td className={`${td} font-mono`}>{o.crewId}</td>
                <td className={`${td} font-semibold`}>{o.disposition}</td>
                <td className={`${td} font-mono`}>
                  {Object.entries(o.tells ?? {}).map(([g, ids]) => (
                    <div key={g}>
                      {g}: {ids.join(', ')}
                    </div>
                  ))}
                </td>
                <td className={`${td} font-mono`}>
                  {Object.entries(o.stances ?? {})
                    .map(([a, s]) => `${a}→${s}`)
                    .join(', ')}
                </td>
                <td className={td}>
                  <Plate s={o.state} />
                </td>
                <td className={td}>
                  {(o.raids ?? []).map((r) => (
                    <div key={r.raidId}>
                      <span className="font-mono">{r.raidId.slice(0, 8)}</span> {r.approach}/
                      {r.stance} → {r.outcome} · {r.kills} kills
                      {r.trades ? ` · ${r.trades} trades` : ''}
                      {r.recruitId ? ' · recruit' : ''}
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Loaded>
  );
}

function RescuesTab({ playerId }: { playerId: string }) {
  const { client, runId } = useRunScope();
  const [open, setOpen] = useState<string | null>(null);
  const key = featureKey(playerId, 'rescues', runId);
  const q = useQuery({
    queryKey: key,
    queryFn: async () => list<RescueAttemptAdminRow>(await playerRescueAttempts(client, playerId)),
  });
  return (
    <Loaded
      q={q}
      endpoint="GET /admin/players/{id}/rescue-attempts"
      owner="S2-CR"
      empty="No rescue attempts this run."
    >
      {(rows) => (
        <table className="w-full text-xs">
          <thead className="text-bruise">
            <tr>
              <th className={th}>When</th>
              <th className={th}>Scenario</th>
              <th className={th}>Source</th>
              <th className={th}>Hidden</th>
              <th className={th}>State</th>
              <th className={th}>Rejected</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <Fragment key={a.id}>
                <tr className="border-t border-newsprint align-top">
                  <td className={`${td} font-mono`}>{when(a.createdAt)}</td>
                  <td className={`${td} font-mono`}>{a.scenarioId}</td>
                  <td className={td}>{a.source}</td>
                  <td className={`${td} font-mono`}>
                    {Object.entries(a.hidden ?? {}).map(([k, v]) => (
                      <div key={k}>
                        {k}={v}
                      </div>
                    ))}
                  </td>
                  <td className={td}>
                    <Plate s={a.state} />
                    {a.claimExpiresAt && (
                      <div className="text-bruise">held until {when(a.claimExpiresAt)}</div>
                    )}
                  </td>
                  <td className={`${td} text-blam`}>{a.rejectReason ?? ''}</td>
                  <td className={`${td} whitespace-nowrap`}>
                    {a.state === 'claim_pending' && (
                      <>
                        <button type="button" className={btn} onClick={() => setOpen(`g:${a.id}`)}>
                          Grant
                        </button>{' '}
                        <button type="button" className={btn} onClick={() => setOpen(`v:${a.id}`)}>
                          Void
                        </button>
                      </>
                    )}
                  </td>
                </tr>
                {(open === `g:${a.id}` || open === `v:${a.id}`) && (
                  <tr className="bg-newsprint/40">
                    <td colSpan={7} className="px-2 py-2">
                      {open === `g:${a.id}` ? (
                        <ReasonEditor
                          queryKey={key}
                          label={`Grant reason for rescue ${a.id}`}
                          button="Grant claim"
                          note="The held survivor joins now (the roster cap is waived for this one)."
                          confirmText={`Grant the held rescue claim from ${a.scenarioId}?`}
                          send={(reason) => grantRescueClaim(client, a.id, reason)}
                          onDone={() => setOpen(null)}
                        />
                      ) : (
                        <ReasonEditor
                          queryKey={key}
                          label={`Void reason for rescue ${a.id}`}
                          button="Void claim"
                          note="The held survivor is gone; nothing joins."
                          confirmText={`Void the held rescue claim from ${a.scenarioId}?`}
                          send={(reason) => voidRescueClaim(client, a.id, reason)}
                          onDone={() => setOpen(null)}
                        />
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </Loaded>
  );
}

function QuestsTab({ playerId }: { playerId: string }) {
  const { client, runId } = useRunScope();
  const [open, setOpen] = useState<string | null>(null);
  const key = featureKey(playerId, 'quests', runId);
  const q = useQuery({
    queryKey: key,
    queryFn: async () => list<QuestAdminRow>(await playerQuests(client, playerId)),
  });
  return (
    <Loaded
      q={q}
      endpoint="GET /admin/players/{id}/quests"
      owner="S2-KEY"
      empty="No key-part hunts this run."
    >
      {(rows) => (
        <div className="space-y-3">
          {rows.map((quest) => (
            <div key={quest.id} className="rounded border border-newsprint p-2">
              <p className="text-sm">
                <span className="font-semibold">Tier {quest.tier} hunt</span>{' '}
                <Plate s={quest.state} /> started {when(quest.startedAt)}
                {quest.completedAt && ` · done ${when(quest.completedAt)}`}
              </p>
              <h4 className="mt-1 text-xs font-semibold uppercase">Sites</h4>
              <table className="w-full text-xs">
                <tbody>
                  {quest.sites.map((s) => (
                    <Fragment key={s.id}>
                      <tr
                        className={`border-t border-newsprint ${s.voided ? 'text-bruise line-through' : ''}`}
                      >
                        <td className={`${td} font-mono`}>{s.partId}</td>
                        <td className={td}>
                          {s.buildingName ??
                            (s.virtualSiteId ? `virtual ${s.virtualSiteId}` : 'unnamed')}{' '}
                          <span className="font-mono text-bruise">#{s.buildingId}</span>
                        </td>
                        <td className={td}>step {s.step}</td>
                        <td className={`${td} font-mono`}>{s.siteKindId ?? ''}</td>
                        <td className={td}>until {when(s.expiresAt)}</td>
                        <td className={td}>
                          {!s.voided && (
                            <button
                              type="button"
                              className={btn}
                              onClick={() => setOpen(`s:${s.id}`)}
                            >
                              Void
                            </button>
                          )}
                        </td>
                      </tr>
                      {open === `s:${s.id}` && (
                        <tr className="bg-newsprint/40">
                          <td colSpan={6} className="px-2 py-2">
                            <ReasonEditor
                              queryKey={key}
                              label={`Void reason for site ${s.id}`}
                              button="Void site"
                              note="The site closes; its pieces return to the hunt and the picker places a new site."
                              confirmText={`Void the ${s.partId} site at #${s.buildingId}?`}
                              send={(reason) => voidSite(client, s.id, reason)}
                              onDone={() => setOpen(null)}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
              <h4 className="mt-1 text-xs font-semibold uppercase">Piece ledger</h4>
              <table className="w-full text-xs">
                <thead className="text-bruise">
                  <tr>
                    <th className={th}>Part</th>
                    <th className={th}>Item</th>
                    <th className={th}>State</th>
                    <th className={th}>Raid</th>
                    <th className={th}>Team</th>
                    <th className={th}>Updated</th>
                    <th className={th} />
                  </tr>
                </thead>
                <tbody>
                  {quest.pieces.map((p) => (
                    <Fragment key={p.id}>
                      <tr className="border-t border-newsprint">
                        <td className={`${td} font-mono`}>{p.partId}</td>
                        <td className={`${td} font-mono`}>{p.itemId}</td>
                        <td className={td}>
                          <Plate s={p.state} />
                        </td>
                        <td className={`${td} font-mono`}>{p.raidId?.slice(0, 8) ?? '-'}</td>
                        <td className={`${td} font-mono`}>{p.teamId?.slice(0, 8) ?? '-'}</td>
                        <td className={td}>{when(p.updatedAt)}</td>
                        <td className={td}>
                          {p.state !== 'acquired' && (
                            <button
                              type="button"
                              className={btn}
                              onClick={() => setOpen(`p:${p.id}`)}
                            >
                              Re-place
                            </button>
                          )}
                        </td>
                      </tr>
                      {open === `p:${p.id}` && (
                        <tr className="bg-newsprint/40">
                          <td colSpan={7} className="px-2 py-2">
                            <ReasonEditor
                              queryKey={key}
                              label={`Re-place reason for piece ${p.id}`}
                              button="Re-place piece"
                              note="The piece goes back to a site (a stuck or lost piece), placed fresh by the server."
                              confirmText={`Re-place ${p.itemId} (${p.state})?`}
                              send={(reason) => replacePiece(client, p.id, reason)}
                              onDone={() => setOpen(null)}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </Loaded>
  );
}

function OrdersTab({ playerId }: { playerId: string }) {
  const { client, runId } = useRunScope();
  const q = useQuery({
    queryKey: featureKey(playerId, 'orders', runId),
    queryFn: async () => list<BuildOrderAdminRow>(await playerBuildOrders(client, playerId)),
  });
  return (
    <Loaded
      q={q}
      endpoint="GET /admin/players/{id}/build-orders"
      owner="S2-TEC"
      empty="No build orders this run."
    >
      {(rows) => (
        <table className="w-full text-xs">
          <thead className="text-bruise">
            <tr>
              <th className={th}>Created</th>
              <th className={th}>Target</th>
              <th className={th}>State</th>
              <th className={th}>Steps</th>
              <th className={th}>Reserved</th>
              <th className={th}>Finishes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.id} className="border-t border-newsprint align-top">
                <td className={`${td} font-mono`}>{when(o.createdAt)}</td>
                <td className={`${td} font-mono`}>
                  {o.quantity}× {o.itemId ?? o.moduleId}
                </td>
                <td className={td}>
                  <Plate s={o.state} />
                </td>
                <td className={td}>
                  {o.steps.map((s) => (
                    <div key={s.idx}>
                      {s.doneAt ? '✓' : '○'} {s.times}×{' '}
                      <span className="font-mono">{s.recipeId}</span> @ {s.station}
                    </div>
                  ))}
                </td>
                <td className={`${td} font-mono`}>
                  {o.reserved.map((r) => `${r.quantity}× ${r.itemId}`).join(', ')}
                  {o.reservedWh ? ` · ${o.reservedWh} Wh` : ''}
                </td>
                <td className={td}>{when(o.finishesAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Loaded>
  );
}
